import React, { useState, useCallback, useEffect } from "react";
import api, { formatVND } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Package, Warehouse, LayoutGrid, FileText, Wallet, Search, Plus,
  Trash2, Pencil, AlertTriangle, ArrowUp, ArrowDown, X, Filter,
  ClipboardCheck, DollarSign, Star, Eye, Upload, ChevronLeft, ChevronRight,
  CheckCircle, XCircle, Clock, FileSpreadsheet,
} from "lucide-react";

const TABS = [
  { key: "inventory", label: "Tồn kho", icon: Warehouse },
  { key: "stock-check", label: "Kiểm kho", icon: ClipboardCheck },
  { key: "price-list", label: "Bảng giá", icon: DollarSign },
  { key: "goods-import", label: "Nhập hàng", icon: ArrowUp },
  { key: "goods-return", label: "Trả hàng", icon: ArrowDown },
  { key: "goods-dispose", label: "Xuất huỷ", icon: Trash2 },
  { key: "tables", label: "Phòng/Bàn", icon: LayoutGrid },
  { key: "invoices", label: "Hoá đơn", icon: FileText },
  { key: "cashbook", label: "Sổ quỹ", icon: Wallet },
];

export default function WarehousePage() {
  const [activeTab, setActiveTab] = useState("inventory");

  return (
    <div className="space-y-4" data-testid="warehouse-page">
      <div>
        <h1 className="font-heading text-2xl font-bold text-[#F8FAFC]">Kho & Tài chính</h1>
        <p className="text-slate-400 text-sm mt-1">Quản lý tồn kho, phòng bàn, hoá đơn, sổ quỹ</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-[#0f172a] border border-[#334155] rounded-lg p-1 overflow-x-auto" data-testid="warehouse-tabs">
        {TABS.map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${
              activeTab === tab.key ? "bg-[#1e293b] text-lime-400" : "text-slate-400 hover:text-slate-200"
            }`} data-testid={`tab-${tab.key}`}>
            <tab.icon className="w-4 h-4" /> {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "inventory" && <InventoryTab />}
      {activeTab === "stock-check" && <StockCheckTab />}
      {activeTab === "price-list" && <PriceListTab />}
      {activeTab === "goods-import" && <GoodsImportTab />}
      {activeTab === "goods-return" && <GoodsReturnTab />}
      {activeTab === "goods-dispose" && <GoodsDisposeTab />}
      {activeTab === "tables" && <TablesTab />}
      {activeTab === "invoices" && <InvoicesTab />}
      {activeTab === "cashbook" && <CashbookTab />}
    </div>
  );
}

// ==================== INVENTORY TAB ====================
function InventoryTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [onlyLow, setOnlyLow] = useState(false);
  const [adjustModal, setAdjustModal] = useState(null);
  const [adjQty, setAdjQty] = useState("");
  const [adjReason, setAdjReason] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (onlyLow) params.set("low_stock", "true");
      if (search) params.set("search", search);
      const { data } = await api.get(`/warehouse/inventory?${params}`);
      setItems(data.items || []);
    } catch { toast.error("Lỗi tải tồn kho"); }
    finally { setLoading(false); }
  }, [search, onlyLow]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAdjust = async (isAdd) => {
    if (!adjQty || !adjustModal) return;
    const change = isAdd ? Math.abs(parseInt(adjQty)) : -Math.abs(parseInt(adjQty));
    try {
      const { data } = await api.post("/warehouse/inventory/adjust", {
        product_id: adjustModal.id, qty_change: change, reason: adjReason,
      });
      toast.success(data.message);
      setAdjustModal(null); setAdjQty(""); setAdjReason("");
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Lỗi"); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-2 bg-[#1e293b] border border-[#334155] rounded-lg px-3 flex-1 max-w-[300px]">
          <Search className="w-4 h-4 text-slate-500" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Tìm SP..."
            className="border-0 bg-transparent text-slate-200 focus-visible:ring-0 h-9 text-xs" data-testid="inv-search" />
        </div>
        <button onClick={() => setOnlyLow(!onlyLow)}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${onlyLow ? "bg-red-500/20 text-red-400 border-red-500/30" : "bg-[#1e293b] text-slate-400 border-[#334155]"}`}
          data-testid="filter-low-stock">
          <AlertTriangle className="w-3 h-3 inline mr-1" /> Sắp hết
        </button>
        <span className="text-xs text-slate-500 ml-auto">{items.length} SP</span>
      </div>

      <div className="bg-[#1e293b] border border-[#334155] rounded-xl overflow-hidden">
        <div className="overflow-x-auto max-h-[calc(100vh-280px)] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-[#0f172a] z-10">
              <tr className="text-slate-400">
                <th className="px-3 py-2 text-left">Sản phẩm</th>
                <th className="px-3 py-2 text-left">Mã</th>
                <th className="px-3 py-2 text-center">Tồn kho</th>
                <th className="px-3 py-2 text-center">Tối thiểu</th>
                <th className="px-3 py-2 text-center">ĐVT</th>
                <th className="px-3 py-2 text-right">Giá vốn</th>
                <th className="px-3 py-2 text-center">Thao tác</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-500">Đang tải...</td></tr> :
              items.length === 0 ? <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-500">Không có SP</td></tr> :
              items.map(item => (
                <tr key={item.id} className="border-t border-[#334155]/30 hover:bg-[#334155]/10">
                  <td className="px-3 py-2 text-slate-200 font-medium max-w-[200px] truncate">{item.name}</td>
                  <td className="px-3 py-2 text-slate-500 font-mono text-[10px]">{item.kv_code || "—"}</td>
                  <td className="px-3 py-2 text-center">
                    <span className={`font-bold ${item.low_stock ? "text-red-400" : "text-lime-400"}`}>{item.stock_qty}</span>
                  </td>
                  <td className="px-3 py-2 text-center text-slate-500">{item.min_stock}</td>
                  <td className="px-3 py-2 text-center text-slate-400">{item.unit || "—"}</td>
                  <td className="px-3 py-2 text-right text-slate-400">{item.cost > 0 ? formatVND(item.cost) + "đ" : "—"}</td>
                  <td className="px-3 py-2 text-center">
                    <button onClick={() => { setAdjustModal(item); setAdjQty(""); setAdjReason(""); }}
                      className="text-lime-400 hover:text-lime-300 text-[10px] font-semibold px-2 py-0.5 rounded bg-lime-500/10" data-testid={`adjust-${item.id}`}>
                      Điều chỉnh
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Adjust Modal */}
      <Dialog open={!!adjustModal} onOpenChange={() => setAdjustModal(null)}>
        <DialogContent className="bg-[#1e293b] border-[#334155] sm:max-w-xs" data-testid="adjust-modal">
          <DialogHeader>
            <DialogTitle className="text-[#F8FAFC] text-sm">Điều chỉnh kho: {adjustModal?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-center text-2xl font-bold text-slate-200">Tồn hiện tại: {adjustModal?.stock_qty} {adjustModal?.unit}</div>
            <Input type="number" value={adjQty} onChange={e => setAdjQty(e.target.value)} placeholder="Số lượng"
              className="bg-[#0f172a] border-[#334155] text-slate-50 h-9 text-center text-lg" data-testid="adj-qty" />
            <Input value={adjReason} onChange={e => setAdjReason(e.target.value)} placeholder="Lý do (tuỳ chọn)"
              className="bg-[#0f172a] border-[#334155] text-slate-50 h-9" data-testid="adj-reason" />
          </div>
          <DialogFooter className="flex gap-2">
            <Button onClick={() => handleAdjust(true)} className="bg-lime-500 text-[#0f172a] font-bold flex-1" data-testid="adj-add">
              <ArrowUp className="w-4 h-4 mr-1" /> Nhập kho
            </Button>
            <Button onClick={() => handleAdjust(false)} className="bg-red-500 text-white font-bold flex-1" data-testid="adj-sub">
              <ArrowDown className="w-4 h-4 mr-1" /> Xuất kho
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ==================== TABLES TAB ====================
function TablesTab() {
  const [tables, setTables] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterArea, setFilterArea] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", area: "", note: "", seats: 0, status: "Đang hoạt động" });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = filterArea ? `?area=${filterArea}` : "";
      const { data } = await api.get(`/warehouse/tables${params}`);
      setTables(data.tables || []);
      setAreas(data.areas || []);
    } catch { toast.error("Lỗi tải phòng/bàn"); }
    finally { setLoading(false); }
  }, [filterArea]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => { setEditing(null); setForm({ name: "", area: "", note: "", seats: 0, status: "Đang hoạt động" }); setModalOpen(true); };
  const openEdit = (t) => { setEditing(t); setForm({ name: t.name, area: t.area, note: t.note, seats: t.seats, status: t.status }); setModalOpen(true); };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Tên bàn không được trống"); return; }
    try {
      if (editing) {
        await api.put(`/warehouse/tables/${editing.id}`, form);
        toast.success("Đã cập nhật");
      } else {
        await api.post("/warehouse/tables", form);
        toast.success("Đã thêm");
      }
      setModalOpen(false); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Lỗi"); }
  };

  const handleDelete = async (id) => {
    if (!confirm("Xoá bàn này?")) return;
    try { await api.delete(`/warehouse/tables/${id}`); toast.success("Đã xoá"); fetchData(); }
    catch (err) { toast.error("Lỗi xoá"); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <select value={filterArea} onChange={e => setFilterArea(e.target.value)}
          className="bg-[#1e293b] border border-[#334155] rounded-lg px-3 py-1.5 text-xs text-slate-200" data-testid="filter-area">
          <option value="">Tất cả khu vực</option>
          {areas.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <span className="text-xs text-slate-500 ml-auto">{tables.length} bàn</span>
        <Button onClick={openCreate} size="sm" className="bg-lime-500 text-[#0f172a] font-semibold text-xs" data-testid="add-table-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> Thêm bàn
        </Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-2">
        {tables.map(t => (
          <div key={t.id} className="bg-[#1e293b] border border-[#334155] rounded-lg p-2.5 text-center group relative hover:border-lime-500/30 transition-colors">
            <div className="text-sm font-bold text-slate-200">{t.name}</div>
            <div className="text-[10px] text-slate-500">{t.area || "—"}</div>
            <div className="text-[10px] text-slate-500 mt-0.5">{t.note}</div>
            <Badge className={`mt-1 text-[9px] border-0 ${t.status === "Đang hoạt động" ? "bg-lime-500/20 text-lime-400" : "bg-slate-500/20 text-slate-400"}`}>
              {t.status === "Đang hoạt động" ? "HĐ" : t.status}
            </Badge>
            <div className="absolute top-1 right-1 hidden group-hover:flex gap-0.5">
              <button onClick={() => openEdit(t)} className="text-slate-400 hover:text-lime-400 p-0.5"><Pencil className="w-3 h-3" /></button>
              <button onClick={() => handleDelete(t.id)} className="text-slate-400 hover:text-red-400 p-0.5"><Trash2 className="w-3 h-3" /></button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="bg-[#1e293b] border-[#334155] sm:max-w-xs">
          <DialogHeader><DialogTitle className="text-[#F8FAFC] text-sm">{editing ? "Sửa" : "Thêm"} phòng/bàn</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} placeholder="Tên bàn (K01, P05...)"
              className="bg-[#0f172a] border-[#334155] text-slate-50 h-9" data-testid="table-name" />
            <Input value={form.area} onChange={e => setForm(f => ({...f, area: e.target.value}))} placeholder="Khu vực"
              className="bg-[#0f172a] border-[#334155] text-slate-50 h-9" data-testid="table-area" />
            <Input value={form.note} onChange={e => setForm(f => ({...f, note: e.target.value}))} placeholder="Ghi chú"
              className="bg-[#0f172a] border-[#334155] text-slate-50 h-9" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setModalOpen(false)} className="text-slate-400">Huỷ</Button>
            <Button onClick={handleSave} className="bg-lime-500 text-[#0f172a] font-bold">{editing ? "Cập nhật" : "Thêm"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ==================== INVOICES TAB ====================
function InvoicesTab() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set("customer", search);
      params.set("limit", "200");
      const { data } = await api.get(`/warehouse/invoices?${params}`);
      setInvoices(data.invoices || []);
    } catch { toast.error("Lỗi tải hoá đơn"); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async (id) => {
    if (!confirm("Xoá hoá đơn?")) return;
    try { await api.delete(`/warehouse/invoices/${id}`); toast.success("Đã xoá"); fetchData(); }
    catch { toast.error("Lỗi"); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-2 bg-[#1e293b] border border-[#334155] rounded-lg px-3 flex-1 max-w-[300px]">
          <Search className="w-4 h-4 text-slate-500" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Tìm theo KH..."
            className="border-0 bg-transparent text-slate-200 focus-visible:ring-0 h-9 text-xs" data-testid="inv-search-customer" />
        </div>
        <span className="text-xs text-slate-500 ml-auto">{invoices.length} hoá đơn</span>
      </div>

      <div className="bg-[#1e293b] border border-[#334155] rounded-xl overflow-hidden">
        <div className="overflow-x-auto max-h-[calc(100vh-280px)] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-[#0f172a] z-10">
              <tr className="text-slate-400">
                <th className="px-3 py-2 text-left">Mã HD</th>
                <th className="px-3 py-2 text-left">Khách hàng</th>
                <th className="px-3 py-2 text-center">SP</th>
                <th className="px-3 py-2 text-right">Tổng tiền</th>
                <th className="px-3 py-2 text-center">Bàn</th>
                <th className="px-3 py-2 text-center">NV</th>
                <th className="px-3 py-2 text-center">TT</th>
                <th className="px-3 py-2 text-center w-16">Thao tác</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-500">Đang tải...</td></tr> :
              invoices.length === 0 ? <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-500">Không có hoá đơn</td></tr> :
              invoices.map(inv => (
                <tr key={inv.id} className="border-t border-[#334155]/30 hover:bg-[#334155]/10 cursor-pointer" onClick={() => setDetail(inv)}>
                  <td className="px-3 py-2 text-slate-300 font-mono">{inv.code}</td>
                  <td className="px-3 py-2 text-slate-200">{inv.customer_name || "Khách lẻ"}</td>
                  <td className="px-3 py-2 text-center text-slate-400">{inv.items_count}</td>
                  <td className="px-3 py-2 text-right text-lime-400 font-medium">{formatVND(inv.total)}đ</td>
                  <td className="px-3 py-2 text-center text-teal-400">{inv.table || "—"}</td>
                  <td className="px-3 py-2 text-center text-slate-500">{inv.staff_create || "—"}</td>
                  <td className="px-3 py-2 text-center">
                    <Badge className={`text-[9px] border-0 ${inv.status === "Hoàn thành" ? "bg-lime-500/20 text-lime-400" : "bg-amber-500/20 text-amber-400"}`}>{inv.status}</Badge>
                  </td>
                  <td className="px-3 py-2 text-center" onClick={e => e.stopPropagation()}>
                    <button onClick={() => handleDelete(inv.id)} className="text-slate-400 hover:text-red-400 p-1"><Trash2 className="w-3 h-3" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Invoice Detail Modal */}
      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="bg-[#1e293b] border-[#334155] sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-[#F8FAFC]">Hoá đơn {detail?.code}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-3 text-xs">
              <div className="grid grid-cols-2 gap-2 bg-[#0f172a] rounded-lg p-3">
                <div><span className="text-slate-500">KH:</span> <span className="text-slate-200">{detail.customer_name || "Khách lẻ"}</span></div>
                <div><span className="text-slate-500">Bàn:</span> <span className="text-teal-400">{detail.table}</span></div>
                <div><span className="text-slate-500">NV:</span> <span className="text-slate-200">{detail.staff_create}</span></div>
                <div><span className="text-slate-500">Số khách:</span> <span className="text-slate-200">{detail.guest_count}</span></div>
                <div><span className="text-slate-500">Tổng:</span> <span className="text-lime-400 font-bold">{formatVND(detail.total)}đ</span></div>
                <div><span className="text-slate-500">TT:</span> <Badge className="text-[9px] bg-lime-500/20 text-lime-400 border-0">{detail.status}</Badge></div>
              </div>
              <table className="w-full">
                <thead><tr className="text-slate-400 border-b border-[#334155]">
                  <th className="py-1 text-left">SP</th><th className="py-1 text-center">SL</th><th className="py-1 text-right">Đơn giá</th><th className="py-1 text-right">Thành tiền</th>
                </tr></thead>
                <tbody>
                  {(detail.items || []).map((item, i) => (
                    <tr key={i} className="border-t border-[#334155]/30">
                      <td className="py-1 text-slate-200">{item.name}</td>
                      <td className="py-1 text-center text-slate-400">{item.qty}</td>
                      <td className="py-1 text-right text-slate-400">{formatVND(item.price)}đ</td>
                      <td className="py-1 text-right text-slate-200">{formatVND(item.total || item.price * item.qty)}đ</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ==================== CASHBOOK TAB ====================
function CashbookTab() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ type: "Phiếu thu", recipient: "", amount: "", note: "" });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = filterType ? `?type_filter=${filterType}` : "";
      const { data } = await api.get(`/warehouse/cashbook${params}`);
      setEntries(data.entries || []);
    } catch { toast.error("Lỗi tải sổ quỹ"); }
    finally { setLoading(false); }
  }, [filterType]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const totalThu = entries.filter(e => e.type?.includes("thu")).reduce((s, e) => s + (e.amount || 0), 0);
  const totalChi = entries.filter(e => e.type?.includes("chi")).reduce((s, e) => s + (e.amount || 0), 0);

  const handleCreate = async () => {
    if (!form.amount) { toast.error("Nhập số tiền"); return; }
    try {
      await api.post("/warehouse/cashbook", { ...form, amount: parseInt(form.amount) || 0 });
      toast.success("Đã tạo phiếu");
      setCreateOpen(false); setForm({ type: "Phiếu thu", recipient: "", amount: "", note: "" });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Lỗi"); }
  };

  const handleDelete = async (id) => {
    if (!confirm("Xoá phiếu?")) return;
    try { await api.delete(`/warehouse/cashbook/${id}`); toast.success("Đã xoá"); fetchData(); }
    catch { toast.error("Lỗi"); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <select value={filterType} onChange={e => setFilterType(e.target.value)}
          className="bg-[#1e293b] border border-[#334155] rounded-lg px-3 py-1.5 text-xs text-slate-200">
          <option value="">Tất cả</option>
          <option value="thu">Phiếu thu</option>
          <option value="chi">Phiếu chi</option>
        </select>
        <div className="flex gap-3 ml-4">
          <span className="text-xs text-lime-400">Thu: {formatVND(totalThu)}đ</span>
          <span className="text-xs text-red-400">Chi: {formatVND(totalChi)}đ</span>
          <span className="text-xs text-slate-200 font-bold">Còn: {formatVND(totalThu - totalChi)}đ</span>
        </div>
        <span className="text-xs text-slate-500 ml-auto">{entries.length} phiếu</span>
        <Button onClick={() => setCreateOpen(true)} size="sm" className="bg-lime-500 text-[#0f172a] font-semibold text-xs" data-testid="add-cashbook-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> Tạo phiếu
        </Button>
      </div>

      <div className="bg-[#1e293b] border border-[#334155] rounded-xl overflow-hidden">
        <div className="overflow-x-auto max-h-[calc(100vh-280px)] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-[#0f172a] z-10">
              <tr className="text-slate-400">
                <th className="px-3 py-2 text-left">Mã phiếu</th>
                <th className="px-3 py-2 text-left">Loại</th>
                <th className="px-3 py-2 text-left">Người nộp/nhận</th>
                <th className="px-3 py-2 text-right">Giá trị</th>
                <th className="px-3 py-2 text-left">Thời gian</th>
                <th className="px-3 py-2 text-center w-12">Xoá</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-500">Đang tải...</td></tr> :
              entries.length === 0 ? <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-500">Không có phiếu</td></tr> :
              entries.map(e => (
                <tr key={e.id} className="border-t border-[#334155]/30 hover:bg-[#334155]/10">
                  <td className="px-3 py-2 text-slate-300 font-mono">{e.code}</td>
                  <td className="px-3 py-2">
                    <Badge className={`text-[9px] border-0 ${e.type?.includes("thu") ? "bg-lime-500/20 text-lime-400" : "bg-red-500/20 text-red-400"}`}>
                      {e.type?.includes("thu") ? "Thu" : "Chi"}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 text-slate-200">{e.recipient || "—"}</td>
                  <td className="px-3 py-2 text-right font-bold text-slate-200">{formatVND(e.amount)}đ</td>
                  <td className="px-3 py-2 text-slate-500">{e.time ? new Date(e.time).toLocaleString("vi-VN", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—"}</td>
                  <td className="px-3 py-2 text-center">
                    <button onClick={() => handleDelete(e.id)} className="text-slate-400 hover:text-red-400 p-1"><Trash2 className="w-3 h-3" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Cashbook Entry */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-[#1e293b] border-[#334155] sm:max-w-xs">
          <DialogHeader><DialogTitle className="text-[#F8FAFC] text-sm">Tạo phiếu thu/chi</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <select value={form.type} onChange={e => setForm(f => ({...f, type: e.target.value}))}
              className="w-full bg-[#0f172a] border border-[#334155] rounded-md px-3 py-2 text-xs text-slate-200">
              <option value="Phiếu thu">Phiếu thu</option>
              <option value="Phiếu chi">Phiếu chi</option>
            </select>
            <Input value={form.recipient} onChange={e => setForm(f => ({...f, recipient: e.target.value}))} placeholder="Người nộp/nhận"
              className="bg-[#0f172a] border-[#334155] text-slate-50 h-9" />
            <Input type="number" value={form.amount} onChange={e => setForm(f => ({...f, amount: e.target.value}))} placeholder="Số tiền"
              className="bg-[#0f172a] border-[#334155] text-slate-50 h-9" />
            <Input value={form.note} onChange={e => setForm(f => ({...f, note: e.target.value}))} placeholder="Ghi chú"
              className="bg-[#0f172a] border-[#334155] text-slate-50 h-9" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreateOpen(false)} className="text-slate-400">Huỷ</Button>
            <Button onClick={handleCreate} className="bg-lime-500 text-[#0f172a] font-bold">Tạo phiếu</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ==================== GOODS IMPORT TAB ====================
function GoodsImportTab() {
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/warehouse/goods-import?limit=200"); setReceipts(data.receipts || []); }
    catch { toast.error("Lỗi tải nhập hàng"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async (id) => {
    if (!confirm("Xoá phiếu nhập?")) return;
    try { await api.delete(`/warehouse/goods-import/${id}`); toast.success("Đã xoá"); fetchData(); }
    catch { toast.error("Lỗi"); }
  };

  return (
    <div className="space-y-3" data-testid="goods-import-tab">
      <div className="flex items-center justify-between">
        <span className="text-xs text-slate-500">{receipts.length} phiếu nhập</span>
      </div>
      <div className="bg-[#1e293b] border border-[#334155] rounded-xl overflow-hidden">
        <div className="overflow-x-auto max-h-[calc(100vh-280px)] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-[#0f172a] z-10">
              <tr className="text-slate-400">
                <th className="px-3 py-2 text-left">Mã phiếu</th>
                <th className="px-3 py-2 text-left">NCC</th>
                <th className="px-3 py-2 text-left">Người nhập</th>
                <th className="px-3 py-2 text-center">Mặt hàng</th>
                <th className="px-3 py-2 text-center">Tổng SL</th>
                <th className="px-3 py-2 text-right">Tổng tiền</th>
                <th className="px-3 py-2 text-left">Ghi chú</th>
                <th className="px-3 py-2 text-center">TT</th>
                <th className="px-3 py-2 text-center w-12">Xoá</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={9} className="px-3 py-8 text-center text-slate-500">Đang tải...</td></tr> :
              receipts.length === 0 ? <tr><td colSpan={9} className="px-3 py-8 text-center text-slate-500">Không có phiếu nhập</td></tr> :
              receipts.map(r => (
                <tr key={r.id} className="border-t border-[#334155]/30 hover:bg-[#334155]/10 cursor-pointer" onClick={() => setDetail(r)}>
                  <td className="px-3 py-2 text-slate-300 font-mono">{r.code}</td>
                  <td className="px-3 py-2 text-slate-200">{r.supplier_name}</td>
                  <td className="px-3 py-2 text-slate-400">{r.staff_name}</td>
                  <td className="px-3 py-2 text-center text-slate-300">{r.total_items}</td>
                  <td className="px-3 py-2 text-center text-lime-400 font-bold">{r.total_qty?.toLocaleString("vi-VN")}</td>
                  <td className="px-3 py-2 text-right text-slate-200">{formatVND(r.total_amount)}đ</td>
                  <td className="px-3 py-2 text-slate-500 max-w-[150px] truncate">{r.note}</td>
                  <td className="px-3 py-2 text-center"><Badge className="bg-lime-500/20 text-lime-400 text-[9px] border-0">{r.status}</Badge></td>
                  <td className="px-3 py-2 text-center" onClick={e => e.stopPropagation()}>
                    <button onClick={() => handleDelete(r.id)} className="text-slate-400 hover:text-red-400 p-1"><Trash2 className="w-3 h-3" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* Detail */}
      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="bg-[#1e293b] border-[#334155] sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-[#F8FAFC]">Phiếu nhập {detail?.code}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-2 text-xs">
              <div className="grid grid-cols-2 gap-2 bg-[#0f172a] rounded-lg p-3">
                <div><span className="text-slate-500">NCC:</span> <span className="text-slate-200">{detail.supplier_name}</span></div>
                <div><span className="text-slate-500">Người nhập:</span> <span className="text-slate-200">{detail.staff_name}</span></div>
                <div><span className="text-slate-500">Ghi chú:</span> <span className="text-slate-200">{detail.note}</span></div>
                <div><span className="text-slate-500">Tổng:</span> <span className="text-lime-400 font-bold">{detail.total_qty} SP</span></div>
              </div>
              <table className="w-full">
                <thead><tr className="text-slate-400 border-b border-[#334155]"><th className="py-1 text-left">Mã</th><th className="py-1 text-left">Tên hàng</th><th className="py-1 text-center">SL</th><th className="py-1 text-left">ĐVT</th></tr></thead>
                <tbody>{(detail.items || []).map((item, i) => (
                  <tr key={i} className="border-t border-[#334155]/30">
                    <td className="py-1 text-slate-400 font-mono">{item.kv_code}</td>
                    <td className="py-1 text-slate-200">{item.name}</td>
                    <td className="py-1 text-center text-lime-400 font-bold">{item.qty}</td>
                    <td className="py-1 text-slate-400">{item.unit}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ==================== GOODS RETURN TAB ====================
function GoodsReturnTab() {
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/warehouse/goods-return?limit=100"); setReceipts(data.receipts || []); }
    catch { toast.error("Lỗi tải trả hàng"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async (id) => {
    if (!confirm("Xoá phiếu trả?")) return;
    try { await api.delete(`/warehouse/goods-return/${id}`); toast.success("Đã xoá"); fetchData(); }
    catch { toast.error("Lỗi"); }
  };

  return (
    <div className="space-y-3" data-testid="goods-return-tab">
      <div className="flex items-center"><span className="text-xs text-slate-500">{receipts.length} phiếu trả hàng</span></div>
      <div className="bg-[#1e293b] border border-[#334155] rounded-xl overflow-hidden">
        <div className="overflow-x-auto max-h-[calc(100vh-280px)] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-[#0f172a] z-10">
              <tr className="text-slate-400">
                <th className="px-3 py-2 text-left">Mã phiếu</th>
                <th className="px-3 py-2 text-left">NCC</th>
                <th className="px-3 py-2 text-left">Người trả</th>
                <th className="px-3 py-2 text-center">SL</th>
                <th className="px-3 py-2 text-left">Ghi chú</th>
                <th className="px-3 py-2 text-center">TT</th>
                <th className="px-3 py-2 text-center w-12">Xoá</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-500">Đang tải...</td></tr> :
              receipts.length === 0 ? <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-500">Không có phiếu trả</td></tr> :
              receipts.map(r => (
                <tr key={r.id} className="border-t border-[#334155]/30 hover:bg-[#334155]/10 cursor-pointer" onClick={() => setDetail(r)}>
                  <td className="px-3 py-2 text-slate-300 font-mono">{r.code}</td>
                  <td className="px-3 py-2 text-slate-200">{r.supplier_name}</td>
                  <td className="px-3 py-2 text-slate-400">{r.staff_name}</td>
                  <td className="px-3 py-2 text-center text-amber-400 font-bold">{r.total_qty}</td>
                  <td className="px-3 py-2 text-slate-500 max-w-[150px] truncate">{r.note}</td>
                  <td className="px-3 py-2 text-center"><Badge className="bg-amber-500/20 text-amber-400 text-[9px] border-0">{r.status}</Badge></td>
                  <td className="px-3 py-2 text-center" onClick={e => e.stopPropagation()}>
                    <button onClick={() => handleDelete(r.id)} className="text-slate-400 hover:text-red-400 p-1"><Trash2 className="w-3 h-3" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="bg-[#1e293b] border-[#334155] sm:max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-[#F8FAFC]">Trả hàng {detail?.code}</DialogTitle></DialogHeader>
          {detail && (
            <table className="w-full text-xs">
              <thead><tr className="text-slate-400 border-b border-[#334155]"><th className="py-1 text-left">Mã</th><th className="py-1 text-left">Tên hàng</th><th className="py-1 text-center">SL</th></tr></thead>
              <tbody>{(detail.items || []).map((item, i) => (
                <tr key={i} className="border-t border-[#334155]/30">
                  <td className="py-1 text-slate-400 font-mono">{item.kv_code}</td>
                  <td className="py-1 text-slate-200">{item.name}</td>
                  <td className="py-1 text-center text-amber-400 font-bold">{item.qty}</td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ==================== GOODS DISPOSE TAB ====================
function GoodsDisposeTab() {
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/warehouse/goods-dispose?limit=200"); setReceipts(data.receipts || []); }
    catch { toast.error("Lỗi tải xuất huỷ"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async (id) => {
    if (!confirm("Xoá phiếu xuất huỷ?")) return;
    try { await api.delete(`/warehouse/goods-dispose/${id}`); toast.success("Đã xoá"); fetchData(); }
    catch { toast.error("Lỗi"); }
  };

  return (
    <div className="space-y-3" data-testid="goods-dispose-tab">
      <div className="flex items-center"><span className="text-xs text-slate-500">{receipts.length} phiếu xuất huỷ</span></div>
      <div className="bg-[#1e293b] border border-[#334155] rounded-xl overflow-hidden">
        <div className="overflow-x-auto max-h-[calc(100vh-280px)] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-[#0f172a] z-10">
              <tr className="text-slate-400">
                <th className="px-3 py-2 text-left">Mã xuất huỷ</th>
                <th className="px-3 py-2 text-left">Chi nhánh</th>
                <th className="px-3 py-2 text-left">Ghi chú</th>
                <th className="px-3 py-2 text-center">Trạng thái</th>
                <th className="px-3 py-2 text-center w-12">Xoá</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-500">Đang tải...</td></tr> :
              receipts.length === 0 ? <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-500">Không có phiếu xuất huỷ</td></tr> :
              receipts.map(r => (
                <tr key={r.id} className="border-t border-[#334155]/30 hover:bg-[#334155]/10">
                  <td className="px-3 py-2 text-slate-300 font-mono">{r.code}</td>
                  <td className="px-3 py-2 text-slate-200">{r.branch}</td>
                  <td className="px-3 py-2 text-slate-400">{r.note}</td>
                  <td className="px-3 py-2 text-center"><Badge className="bg-red-500/20 text-red-400 text-[9px] border-0">{r.status}</Badge></td>
                  <td className="px-3 py-2 text-center">
                    <button onClick={() => handleDelete(r.id)} className="text-slate-400 hover:text-red-400 p-1"><Trash2 className="w-3 h-3" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}



// ==================== STOCK CHECK TAB (Kiểm kho) ====================
function StockCheckTab() {
  const [checks, setChecks] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [importing, setImporting] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchChecks = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ page, limit: 20 });
      if (statusFilter) p.set("status", statusFilter);
      if (search) p.set("search", search);
      const { data } = await api.get(`/inventory/checks?${p}`);
      setChecks(data.checks || []);
      setTotal(data.total || 0);
      setTotalPages(data.pages || 1);
      setStats(data.stats || {});
    } catch { toast.error("Lỗi tải kiểm kho"); }
    finally { setLoading(false); }
  }, [page, statusFilter, search]);

  useEffect(() => { fetchChecks(); }, [fetchChecks]);

  const handleImport = async () => {
    setImporting(true);
    try {
      const { data } = await api.post("/inventory/import-checks");
      toast.success(data.message);
      fetchChecks();
    } catch (err) { toast.error(err.response?.data?.detail || "Lỗi import kiểm kho"); }
    finally { setImporting(false); }
  };

  const loadDetail = async (id) => {
    setDetailLoading(true);
    try {
      const { data } = await api.get(`/inventory/checks/${id}`);
      setDetail(data);
    } catch { toast.error("Lỗi tải chi tiết"); }
    finally { setDetailLoading(false); }
  };

  const statusBadge = (s) => {
    if (s === "balanced") return <Badge className="bg-lime-500/20 text-lime-400 text-[9px] border-0">Đã cân bằng</Badge>;
    if (s === "cancelled") return <Badge className="bg-red-500/20 text-red-400 text-[9px] border-0">Đã huỷ</Badge>;
    return <Badge className="bg-amber-500/20 text-amber-400 text-[9px] border-0">Nháp</Badge>;
  };

  return (
    <div className="space-y-3" data-testid="stock-check-tab">
      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-3">
          <p className="text-[10px] text-slate-500 uppercase tracking-wider">Tổng phiếu</p>
          <p className="text-xl font-bold text-slate-200 mt-1">{stats.total || 0}</p>
        </div>
        <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-3">
          <p className="text-[10px] text-slate-500 uppercase tracking-wider">Đã cân bằng</p>
          <p className="text-xl font-bold text-lime-400 mt-1">{stats.balanced || 0}</p>
        </div>
        <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-3">
          <p className="text-[10px] text-slate-500 uppercase tracking-wider">Nháp</p>
          <p className="text-xl font-bold text-amber-400 mt-1">{stats.draft || 0}</p>
        </div>
        <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-3">
          <p className="text-[10px] text-slate-500 uppercase tracking-wider">Đã huỷ</p>
          <p className="text-xl font-bold text-red-400 mt-1">{stats.cancelled || 0}</p>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1 bg-[#0f172a] border border-[#334155] rounded-lg px-3 flex-1 min-w-[200px] max-w-[300px]">
          <Search className="w-4 h-4 text-slate-500 shrink-0" />
          <Input value={search} onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === "Enter" && fetchChecks()}
            placeholder="Tìm mã phiếu, người tạo..."
            className="border-0 bg-transparent text-slate-200 placeholder:text-slate-600 focus-visible:ring-0 h-8 text-xs" data-testid="search-stock-check" />
        </div>
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
          className="bg-[#0f172a] border border-[#334155] rounded-lg px-2 py-1.5 text-xs text-slate-200 h-8" data-testid="filter-check-status">
          <option value="">Tất cả trạng thái</option>
          <option value="balanced">Đã cân bằng</option>
          <option value="draft">Nháp</option>
          <option value="cancelled">Đã huỷ</option>
        </select>
        <Button onClick={handleImport} disabled={importing}
          className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs gap-1.5 h-8" data-testid="import-checks-btn">
          <Upload className="w-3.5 h-3.5" /> {importing ? "Đang import..." : "Import từ Excel"}
        </Button>
      </div>

      {/* Table */}
      <div className="bg-[#1e293b] border border-[#334155] rounded-xl overflow-hidden">
        <div className="overflow-x-auto max-h-[calc(100vh-380px)] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-[#0f172a] z-10">
              <tr className="text-slate-400">
                <th className="px-3 py-2.5 text-left">Mã phiếu</th>
                <th className="px-3 py-2.5 text-left">Ngày tạo</th>
                <th className="px-3 py-2.5 text-left">Người tạo</th>
                <th className="px-3 py-2.5 text-left">Người cân bằng</th>
                <th className="px-3 py-2.5 text-center">Số SP</th>
                <th className="px-3 py-2.5 text-right">Chênh lệch</th>
                <th className="px-3 py-2.5 text-center">Trạng thái</th>
                <th className="px-3 py-2.5 text-left max-w-[200px]">Ghi chú</th>
                <th className="px-3 py-2.5 text-center w-12"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={9} className="px-3 py-12 text-center text-slate-500">Đang tải...</td></tr> :
              checks.length === 0 ? <tr><td colSpan={9} className="px-3 py-12 text-center text-slate-500">Không có phiếu kiểm kho</td></tr> :
              checks.map(c => (
                <tr key={c.id} className="border-t border-[#334155]/30 hover:bg-[#334155]/10 cursor-pointer" onClick={() => loadDetail(c.id)}>
                  <td className="px-3 py-2 text-lime-400 font-mono font-medium">{c.code}</td>
                  <td className="px-3 py-2 text-slate-300 whitespace-nowrap">{c.created_at?.split("T")[0] || ""}</td>
                  <td className="px-3 py-2 text-slate-200">{c.created_by}</td>
                  <td className="px-3 py-2 text-slate-400">{c.balanced_by || "---"}</td>
                  <td className="px-3 py-2 text-center text-slate-300">{c.total_items}</td>
                  <td className="px-3 py-2 text-right">
                    <span className={c.total_diff > 0 ? "text-amber-400" : "text-slate-500"}>{c.total_diff > 0 ? c.total_diff.toLocaleString("vi-VN") : "0"}</span>
                  </td>
                  <td className="px-3 py-2 text-center">{statusBadge(c.status)}</td>
                  <td className="px-3 py-2 text-slate-500 max-w-[200px] truncate" title={c.note}>{c.note || "---"}</td>
                  <td className="px-3 py-2 text-center"><Eye className="w-3.5 h-3.5 text-slate-500 hover:text-lime-400 mx-auto" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-2 border-t border-[#334155] bg-[#0f172a]/50">
            <span className="text-[10px] text-slate-500">Trang {page}/{totalPages} — {total} phiếu</span>
            <div className="flex gap-1">
              <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="h-7 px-2 text-slate-400"><ChevronLeft className="w-3.5 h-3.5" /></Button>
              <Button size="sm" variant="ghost" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="h-7 px-2 text-slate-400"><ChevronRight className="w-3.5 h-3.5" /></Button>
            </div>
          </div>
        )}
      </div>

      {/* Detail Dialog */}
      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="bg-[#1e293b] border-[#334155] text-slate-200 max-w-4xl max-h-[80vh] overflow-y-auto">
          {detailLoading ? <p className="text-center text-slate-500 py-8">Dang tai...</p> : detail && (
            <>
              <DialogHeader>
                <DialogTitle className="text-lime-400 flex items-center gap-2">
                  <ClipboardCheck className="w-5 h-5" /> Phiếu kiểm kho {detail.code}
                </DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
                <div><p className="text-[10px] text-slate-500">Người tạo</p><p className="text-sm text-slate-200">{detail.created_by}</p></div>
                <div><p className="text-[10px] text-slate-500">Người cân bằng</p><p className="text-sm text-slate-200">{detail.balanced_by || "—"}</p></div>
                <div><p className="text-[10px] text-slate-500">Trạng thái</p>{statusBadge(detail.status)}</div>
                <div><p className="text-[10px] text-slate-500">Ghi chú</p><p className="text-sm text-slate-300">{detail.note || "—"}</p></div>
              </div>
              <div className="grid grid-cols-3 gap-3 mt-3">
                <div className="bg-[#0f172a] rounded-lg p-2.5 text-center">
                  <p className="text-[10px] text-slate-500">Tổng SP</p>
                  <p className="text-lg font-bold text-slate-200">{detail.total_items}</p>
                </div>
                <div className="bg-[#0f172a] rounded-lg p-2.5 text-center">
                  <p className="text-[10px] text-slate-500">SL chênh lệch</p>
                  <p className="text-lg font-bold text-amber-400">{detail.total_diff}</p>
                </div>
                <div className="bg-[#0f172a] rounded-lg p-2.5 text-center">
                  <p className="text-[10px] text-slate-500">Giá trị lệch</p>
                  <p className="text-lg font-bold text-red-400">{formatVND(detail.total_diff_value)}</p>
                </div>
              </div>
              {/* Items table */}
              <div className="mt-3 overflow-x-auto max-h-[40vh] overflow-y-auto border border-[#334155] rounded-lg">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-[#0f172a] z-10">
                    <tr className="text-slate-400">
                      <th className="px-3 py-2 text-left">#</th>
                      <th className="px-3 py-2 text-left">Mã hàng</th>
                      <th className="px-3 py-2 text-left">Tên hàng</th>
                      <th className="px-3 py-2 text-right">Tồn kho</th>
                      <th className="px-3 py-2 text-right">Thực tế</th>
                      <th className="px-3 py-2 text-right">SL lệch</th>
                      <th className="px-3 py-2 text-right">GT lệch</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(detail.items || []).map((it, i) => (
                      <tr key={i} className="border-t border-[#334155]/30 hover:bg-[#334155]/10">
                        <td className="px-3 py-1.5 text-slate-600">{i + 1}</td>
                        <td className="px-3 py-1.5 text-slate-300 font-mono text-[10px]">{it.product_code}</td>
                        <td className="px-3 py-1.5 text-slate-200">{it.product_name}</td>
                        <td className="px-3 py-1.5 text-right text-slate-400">{it.system_stock?.toLocaleString("vi-VN")}</td>
                        <td className="px-3 py-1.5 text-right text-slate-200">{it.actual_qty?.toLocaleString("vi-VN")}</td>
                        <td className={`px-3 py-1.5 text-right font-medium ${it.diff > 0 ? "text-lime-400" : it.diff < 0 ? "text-red-400" : "text-slate-500"}`}>
                          {it.diff > 0 ? "+" : ""}{it.diff?.toLocaleString("vi-VN")}
                        </td>
                        <td className={`px-3 py-1.5 text-right ${it.diff_value > 0 ? "text-lime-400" : it.diff_value < 0 ? "text-red-400" : "text-slate-500"}`}>
                          {it.diff_value ? formatVND(it.diff_value) : "0"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ==================== PRICE LIST TAB (Bang gia) ====================
function PriceListTab() {
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailItems, setDetailItems] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailPage, setDetailPage] = useState(1);
  const [detailTotalPages, setDetailTotalPages] = useState(1);
  const [detailTotal, setDetailTotal] = useState(0);
  const [detailSearch, setDetailSearch] = useState("");

  const fetchLists = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/inventory/price-lists");
      setLists(data.price_lists || []);
    } catch { toast.error("Lỗi tải bảng giá"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchLists(); }, [fetchLists]);

  const handleImport = async () => {
    setImporting(true);
    try {
      const { data } = await api.post("/inventory/import-prices");
      toast.success(data.message);
      fetchLists();
    } catch (err) { toast.error(err.response?.data?.detail || "Lỗi import bảng giá"); }
    finally { setImporting(false); }
  };

  const loadDetail = useCallback(async (id, pg = 1, srch = "") => {
    setDetailLoading(true);
    try {
      const p = new URLSearchParams({ page: pg, limit: 50 });
      if (srch) p.set("search", srch);
      const { data } = await api.get(`/inventory/price-lists/${id}?${p}`);
      setDetail(data);
      setDetailItems(data.items || []);
      setDetailPage(data.page || 1);
      setDetailTotalPages(data.pages || 1);
      setDetailTotal(data.total_items || 0);
    } catch { toast.error("Loi tai chi tiet bang gia"); }
    finally { setDetailLoading(false); }
  }, []);

  const handleSetDefault = async (id) => {
    try {
      await api.post(`/inventory/price-lists/${id}/set-default`);
      toast.success("Đã đặt bảng giá mặc định");
      fetchLists();
    } catch { toast.error("Lỗi"); }
  };

  const handleDelete = async (id) => {
    if (!confirm("Xoá bảng giá này?")) return;
    try {
      await api.delete(`/inventory/price-lists/${id}`);
      toast.success("Đã xoá");
      fetchLists();
    } catch { toast.error("Lỗi xoá"); }
  };

  return (
    <div className="space-y-3" data-testid="price-list-tab">
      {/* Toolbar */}
      <div className="flex items-center gap-2">
        <span className="text-xs text-slate-500">{lists.length} bảng giá</span>
        <div className="ml-auto flex gap-2">
          <Button onClick={handleImport} disabled={importing}
            className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs gap-1.5 h-8" data-testid="import-prices-btn">
            <Upload className="w-3.5 h-3.5" /> {importing ? "Đang import..." : "Import từ Excel"}
          </Button>
        </div>
      </div>

      {/* Price Lists Cards */}
      {loading ? <p className="text-center text-slate-500 py-8">Đang tải...</p> :
      lists.length === 0 ? (
        <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-8 text-center">
          <FileSpreadsheet className="w-10 h-10 text-slate-600 mx-auto mb-2" />
          <p className="text-slate-500 text-sm">Chưa có bảng giá nào. Bấm "Import từ Excel" để bắt đầu.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {lists.map(pl => (
            <div key={pl.id} className={`bg-[#1e293b] border rounded-xl p-4 cursor-pointer hover:border-lime-500/30 transition-colors ${pl.is_default ? "border-lime-500/50" : "border-[#334155]"}`}
              onClick={() => { setDetailSearch(""); loadDetail(pl.id); }} data-testid={`price-list-${pl.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h3 className="text-sm font-semibold text-slate-200 flex items-center gap-1.5">
                    {pl.name}
                    {pl.is_default && <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />}
                  </h3>
                  <p className="text-[10px] text-slate-500 mt-0.5">{pl.description}</p>
                </div>
                <div className="flex gap-1">
                  {!pl.is_default && (
                    <button onClick={(e) => { e.stopPropagation(); handleSetDefault(pl.id); }}
                      className="text-slate-500 hover:text-amber-400 p-1" title="Đặt mặc định" data-testid={`set-default-${pl.id}`}>
                      <Star className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <button onClick={(e) => { e.stopPropagation(); handleDelete(pl.id); }}
                    className="text-slate-500 hover:text-red-400 p-1" title="Xoá" data-testid={`del-pl-${pl.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <Badge className="bg-sky-500/20 text-sky-400 text-[9px] border-0">{pl.total_items} sản phẩm</Badge>
                <span className="text-[10px] text-slate-600">{pl.created_by}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Detail Dialog */}
      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="bg-[#1e293b] border-[#334155] text-slate-200 max-w-5xl max-h-[85vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="text-lime-400 flex items-center gap-2">
                  <DollarSign className="w-5 h-5" /> {detail.name}
                  {detail.is_default && <Star className="w-4 h-4 text-amber-400 fill-amber-400" />}
                </DialogTitle>
              </DialogHeader>
              <p className="text-xs text-slate-500">{detail.description} — {detail.all_items_count} sản phẩm</p>

              {/* Search within price list */}
              <div className="flex items-center gap-2 mt-2">
                <div className="flex items-center gap-1 bg-[#0f172a] border border-[#334155] rounded-lg px-3 flex-1 max-w-[300px]">
                  <Search className="w-4 h-4 text-slate-500 shrink-0" />
                  <Input value={detailSearch}
                    onChange={e => setDetailSearch(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") { setDetailPage(1); loadDetail(detail.id, 1, detailSearch); } }}
                    placeholder="Tìm sản phẩm trong bảng giá..."
                    className="border-0 bg-transparent text-slate-200 placeholder:text-slate-600 focus-visible:ring-0 h-8 text-xs" data-testid="search-price-detail" />
                </div>
                <Button size="sm" onClick={() => { setDetailPage(1); loadDetail(detail.id, 1, detailSearch); }}
                  className="bg-lime-500/20 text-lime-400 hover:bg-lime-500/30 h-8 px-3 text-xs">Tìm</Button>
              </div>

              {/* Items table */}
              <div className="mt-2 overflow-x-auto max-h-[50vh] overflow-y-auto border border-[#334155] rounded-lg">
                {detailLoading ? <p className="text-center py-8 text-slate-500">Đang tải...</p> : (
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-[#0f172a] z-10">
                      <tr className="text-slate-400">
                        <th className="px-3 py-2 text-left">#</th>
                        <th className="px-3 py-2 text-left">Mã hàng</th>
                        <th className="px-3 py-2 text-left">Tên hàng</th>
                        <th className="px-3 py-2 text-right">Giá thiết lập</th>
                        <th className="px-3 py-2 text-right">Giá vốn</th>
                        <th className="px-3 py-2 text-right">ĐG nhập cuối</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detailItems.length === 0 ? (
                        <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-500">Không có sản phẩm</td></tr>
                      ) : detailItems.map((it, i) => (
                        <tr key={i} className="border-t border-[#334155]/30 hover:bg-[#334155]/10">
                          <td className="px-3 py-1.5 text-slate-600">{(detailPage - 1) * 50 + i + 1}</td>
                          <td className="px-3 py-1.5 text-slate-300 font-mono text-[10px]">{it.product_code}</td>
                          <td className="px-3 py-1.5 text-slate-200">{it.product_name}</td>
                          <td className="px-3 py-1.5 text-right text-lime-400 font-medium">{it.price > 0 ? formatVND(it.price) + "đ" : "—"}</td>
                          <td className="px-3 py-1.5 text-right text-slate-400">{it.cost > 0 ? formatVND(it.cost) + "đ" : "—"}</td>
                          <td className="px-3 py-1.5 text-right text-slate-500">{it.last_import_price > 0 ? formatVND(it.last_import_price) + "đ" : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              {detailTotalPages > 1 && (
                <div className="flex items-center justify-between mt-2">
                  <span className="text-[10px] text-slate-500">Trang {detailPage}/{detailTotalPages} — {detailTotal} SP</span>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" disabled={detailPage <= 1}
                      onClick={() => { const np = detailPage - 1; setDetailPage(np); loadDetail(detail.id, np, detailSearch); }}
                      className="h-7 px-2 text-slate-400"><ChevronLeft className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" variant="ghost" disabled={detailPage >= detailTotalPages}
                      onClick={() => { const np = detailPage + 1; setDetailPage(np); loadDetail(detail.id, np, detailSearch); }}
                      className="h-7 px-2 text-slate-400"><ChevronRight className="w-3.5 h-3.5" /></Button>
                  </div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
